/**
 * Widget Color System
 * 
 * Defines a consistent color palette for all dashboard widgets:
 * - Accent color for cards (headers, borders, highlights)
 * - Pastel colors for charts (soft, muted, beautiful)
 */

// Accent color from theme (teal/green)
export const ACCENT_COLOR = "hsl(var(--accent))";
export const ACCENT_FOREGROUND = "hsl(var(--accent-foreground))";

// Unified pastel color palette for charts
// Higher contrast pastels that maintain softness while ensuring label visibility
export const PASTEL_COLORS = {
  // Primary pastels (blues/greens) - enhanced contrast
  teal: "#4ECDC4",      // Teal (more saturated)
  mint: "#6BCF9F",      // Mint green (more saturated)
  sky: "#5DADE2",       // Sky blue (more saturated)
  lavender: "#A569BD",  // Lavender (more saturated)
  
  // Secondary pastels (warm tones) - enhanced contrast
  peach: "#F8A07E",     // Peach (more saturated)
  coral: "#F1948A",     // Coral (more saturated)
  rose: "#F48FB1",      // Rose pink (more saturated)
  cream: "#FAD7A0",     // Cream/amber (more saturated)
  
  // Tertiary pastels (yellows/oranges) - enhanced contrast
  lemon: "#F7DC6F",     // Lemon yellow (more saturated)
  apricot: "#F5B041",   // Apricot (more saturated)
  honey: "#F4D03F",     // Honey yellow (more saturated)
  
  // Neutral pastels - enhanced contrast
  gray: "#BDC3C7",      // Light gray (more saturated)
  blueGray: "#85C1E2",  // Blue-gray (more saturated)
  warmGray: "#D5DBDB",  // Warm gray (more saturated)
};

// Semantic pastel colors for specific use cases
export const SEMANTIC_PASTELS = {
  // Success/Positive (green tones) - enhanced contrast
  success: PASTEL_COLORS.mint,
  successDark: "#52B788",
  
  // Warning/Caution (yellow/orange tones) - enhanced contrast
  warning: PASTEL_COLORS.lemon,
  warningDark: "#F4D03F",
  
  // Error/Negative (coral/rose tones) - enhanced contrast
  error: PASTEL_COLORS.coral,
  errorDark: "#E74C3C",
  
  // Info/Neutral (blue tones) - enhanced contrast
  info: PASTEL_COLORS.sky,
  infoDark: "#3498DB",
};

// Unified chart color sequence - used consistently across all charts
// This ensures visual consistency and better contrast for labels
export const UNIFIED_CHART_COLORS = [
  PASTEL_COLORS.teal,      // 0 - Teal
  PASTEL_COLORS.sky,       // 1 - Sky blue
  PASTEL_COLORS.lavender,  // 2 - Lavender
  PASTEL_COLORS.peach,     // 3 - Peach
  PASTEL_COLORS.coral,     // 4 - Coral
  PASTEL_COLORS.lemon,     // 5 - Lemon yellow
  PASTEL_COLORS.mint,      // 6 - Mint green
  PASTEL_COLORS.rose,      // 7 - Rose pink
  PASTEL_COLORS.apricot,   // 8 - Apricot
  PASTEL_COLORS.blueGray,  // 9 - Blue-gray
];

// Chart color sequences (for multi-series charts)
export const CHART_COLOR_SEQUENCES = {
  // Default sequence - unified colors
  default: UNIFIED_CHART_COLORS,
  
  // Warm sequence
  warm: [
    PASTEL_COLORS.peach,
    PASTEL_COLORS.coral,
    PASTEL_COLORS.rose,
    PASTEL_COLORS.apricot,
    PASTEL_COLORS.honey,
    PASTEL_COLORS.lemon,
  ],
  
  // Cool sequence
  cool: [
    PASTEL_COLORS.teal,
    PASTEL_COLORS.mint,
    PASTEL_COLORS.sky,
    PASTEL_COLORS.lavender,
    PASTEL_COLORS.blueGray,
    PASTEL_COLORS.gray,
  ],
  
  // Balanced sequence (mix of warm and cool) - same as default
  balanced: UNIFIED_CHART_COLORS,
};

// Helper function to get a color from the unified sequence by index
export function getChartColor(index: number, sequence: keyof typeof CHART_COLOR_SEQUENCES = "default"): string {
  const colors = CHART_COLOR_SEQUENCES[sequence];
  return colors[index % colors.length];
}

// Helper function to get unified chart color (preferred method)
export function getUnifiedChartColor(index: number): string {
  return UNIFIED_CHART_COLORS[index % UNIFIED_CHART_COLORS.length];
}

// Helper function to get semantic color
export function getSemanticColor(type: keyof typeof SEMANTIC_PASTELS): string {
  return SEMANTIC_PASTELS[type];
}

